import { useState } from "react";
import { Alert, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import { BucketAwtENUM, useUploadFile } from "@/shared";
import { useUpdateOrdenTrabajo } from "@/shared/actions/erp";
import { CustomPaperBox } from "@/shared/components/ui";
import { ScrollableDialogProps } from "@/shared/components/ui/Dialogs";
import { useUiStore } from "@/store/ui";
import { OrdenTrabajo } from "@/shared/interfaces";
import FotosVisitaMultiUploader from "./FotosVisitaMultiUploader";
import { subirFotosVisita } from "./fotos-visita.utils";

const MIN = 2;
const MAX = 2;

type IniciarVisitaFotosModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  ordenTrabajo?: OrdenTrabajo;
  location: { lat: number; lng: number } | null;
  username?: string;
};

/**
 * Modal para INICIAR la visita: exige subir "Fotos de ingreso" (mín 2, máx 2)
 * y registra hora de inicio + coordenadas. Al confirmar, sube a MinIO y hace el
 * PUT de la OT con `urls_fotos_ingreso`, `fecha_hora_inicio_visita`, `coordenadas`.
 */
export default function IniciarVisitaFotosModal({
  open,
  onClose,
  onSuccess,
  ordenTrabajo,
  location,
  username,
}: IniciarVisitaFotosModalProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [showError, setShowError] = useState(false);
  const setIsGlobalLoading = useUiStore((s) => s.setIsGlobalLoading);
  const { handleUploadFile } = useUploadFile();
  const updateOT = useUpdateOrdenTrabajo<any>({
    enableRedirect: false,
    customSuccessMessage: "Visita iniciada correctamente",
  });

  const PATH_BUCKET = `${BucketAwtENUM.IMAGE_INSTALACION}/${ordenTrabajo?.id_orden_trabajo}`;

  const handleConfirm = async () => {
    if (files.length < MIN) {
      setShowError(true);
      return;
    }
    if (!location) {
      toast.error("No se pudo obtener la ubicación actual.");
      return;
    }
    setIsGlobalLoading(true);
    try {
      const fotos = await subirFotosVisita(files, PATH_BUCKET, handleUploadFile, "Ingreso");
      const historial = ordenTrabajo?.historial_cambios_coordenadas
        ? [...ordenTrabajo.historial_cambios_coordenadas]
        : [];
      historial.push({
        usuario: username || "",
        fecha_cambio: new Date().toISOString(),
        coordenada_anterior: ordenTrabajo?.coordenadas || "",
        coordenada_nueva: `${location.lat},${location.lng}`,
      });
      await updateOT.mutateAsync({
        id: ordenTrabajo?.id_orden_trabajo!,
        data: {
          fecha_hora_inicio_visita: dayjs(),
          coordenadas: `${location.lat},${location.lng}`,
          historial_cambios_coordenadas: historial,
          urls_fotos_ingreso: fotos,
        },
      });
      setFiles([]);
      setShowError(false);
      onClose();
      onSuccess();
    } catch (e) {
      console.error("Error al iniciar visita", e);
      toast.error("Error al iniciar la visita o subir las fotos.");
    } finally {
      setIsGlobalLoading(false);
    }
  };

  return (
    <ScrollableDialogProps
      open={open}
      title="Iniciar Visita — Fotos de ingreso"
      minWidth="60%"
      contentNode={
        <CustomPaperBox sxPaper={{ p: 3 }}>
          <Grid item xs={12}>
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              Se registrará la hora de inicio y las coordenadas
              {location ? ` (${location.lat.toFixed(6)}, ${location.lng.toFixed(6)})` : ""}.
              Sube las <b>fotos de ingreso</b> (mín {MIN}, máx {MAX}).
            </Alert>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Agregar fotos de EPPs, coordenadas o equipos
            </Typography>
            <FotosVisitaMultiUploader
              files={files}
              setFiles={setFiles}
              min={MIN}
              max={MAX}
              label="Fotos de ingreso"
            />
            {showError && files.length < MIN && (
              <Typography color="error" fontWeight={700} sx={{ mt: 1 }}>
                Debes subir al menos {MIN} fotos de ingreso para iniciar la visita.
              </Typography>
            )}
          </Grid>
        </CustomPaperBox>
      }
      onClose={onClose}
      onConfirm={handleConfirm}
      confirmTextBtn="Iniciar visita"
      confirmVariantBtn="outlined"
    />
  );
}
